import styles from "./style.module.scss";
import cx from "classnames";
import { ChangeEvent, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Col, FormGroup, Input, InputGroup, InputGroupText, Label, Row, Table } from "reactstrap";
import { showAlert } from "@utils/Alert";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import DropdownActionsMenu from "@components/content/DropdownMenu";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { clearProductExemptionData, makeProductsExempted, ProductPropertyDataProp, removeProduct, removeZeroProductLines, reorderProductsByIds, resetProductsExemption, setDocumentDiscount, setDocumentDiscountCode, setDocumentDiscountType, setDocumentExempted, setProductExempted, setProductPropertiesData, setProductPropertyData, splitProduct, updateCreditNoteData } from "@/store/slices/eDocumentSlides";
import { EDocumentProductDataType } from "@/formTypes/EDocumentProductDataType";
import { ProductExemptionDataType } from "@/formTypes/ProductExemptionDataType";
import { formatMoneyAmount } from "@utils/EDocument";
import { DISCOUNT_CODES, EINVOICE_EXPORT_INVOICE_TYPE_CODE, FORMULA_PRICES_MANAGEMENT_CODE, MERCHANDISE_PRODUCT_TYPE_CODE, ON_INVOICE_DISCOUNT_TYPE_CODE, TAXES_PERCENTAGES_VALUES } from "@utils/Constants";
import ChangeProductTaxOffCanvas from "@components/layout/Offcanvas/ChangeProductTaxOffCanvas";
import { ProductTaxChangeDataType } from "@/formTypes/ProductTaxChangeDataType";
import { ConfirmActionPubSub, FlushDocumentProductEditsPubSub, OpenAddProductPubSub, OpenDocumentExemptionPubSub, OpenDocumentOtherChargesPubSub, OpenProductPartidaArancelariaPubSub, OpenProductTaxChangePubSub, OpenSetInvoiceDiscountTypePubSub, ScrollToDiscountCodeSelectPubSub } from "@utils/pubsubs/PubSub";
import {
  EditableProductField,
  getPendingDocumentDiscount,
  getPendingProductUpdates,
  parseProductFieldValue,
} from "./commitProductEdits";
import { store } from "store";
import Select from "react-select";
import { generateSelectOption, SelectOptionType } from "@utils/Utils";
import { formatCant } from "@utils/Cant";
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { SortableRow } from "@components/content/SortableRow";
import SetInvoiceDiscountTypeOffCanvas from "@components/layout/Offcanvas/SetInvoiceDiscountTypeOffCanvas";
import { InvoiceDiscountTypeDataType } from "@/formTypes/InvoiceDiscountTypeDataType";
import { selectDocumentSummary, selectDocumentVentaNeta } from "@/store/slices/utils/eDocumentSelectors";
import { getOtherChargeTypeLabel } from "@utils/Constants";
import { resolveChargeAmount } from "@utils/DocumentOtherCharges";
import SetProductPartidaArancelariaOffCanvas from "@components/layout/Offcanvas/SetProductPartidaArancelariaOffCanvas";
import AddProductToDocumentOffCanvas from "@components/layout/Offcanvas/AddProductToDocumentOffCanvas";
import { ProductApi } from "@/api/ProductsApi";
import ActionConfirmation from "@components/content/ActionConfirmation";

type EdocumentProductListProps = {
  documentNoteTable?: boolean,
  priceNote?: boolean,
  providerDocument?: boolean
  hideSelectProduct?: boolean
  /** Muestra la fila de "Otros cargos" en el panel de totales (nodo OtrosCargos v4.4). */
  showOtherCharges?: boolean
}

const EdocumentProductList = (props: EdocumentProductListProps) => {
  const { documentNoteTable = false, priceNote = false, providerDocument = false, hideSelectProduct = false, showOtherCharges = false } = props;
  const dispatch = useAppDispatch();
  const documentProductsSource = useAppSelector(
    (state) => state.eDocument.documentProducts ?? [],
  );
  const documentProducts = useMemo(
    () =>
      [...documentProductsSource].sort(
        (a: EDocumentProductDataType, b: EDocumentProductDataType) =>
          Number(a.orderIndex || 0) - Number(b.orderIndex || 0),
      ),
    [documentProductsSource],
  );
  const lowStockProducts = useAppSelector((state) =>
    state.eDocument.lowStockProducts || []);
  const priceZeroProducts = useAppSelector((state) =>
    state.eDocument.priceZeroProducts || []);
  const noPartidaArancelariaProducts = useAppSelector((state) =>
    state.eDocument.noPartidaArancelariaProducts || []);
  const invalidCabysProducts = useAppSelector((state) =>
    state.eDocument.invalidCabysProducts || []);
  const currencySymbol = useAppSelector((state) => state.currency.currencySymbol) || "";
  const documentDiscount = useAppSelector((state) => state.eDocument.documentDiscount) || Number(0);
  const summaryTotals = useAppSelector(selectDocumentSummary);
  const documentOtherCharges = useAppSelector((state) => state.eDocument.documentOtherCharges);
  const otherChargesBase = useAppSelector(selectDocumentVentaNeta);
  const pricemanaged = useAppSelector((state) => state.suscription.suscription?.pricemanaged);
  const documentDiscountCode = useAppSelector((state) => state.eDocument.documentDiscountCode);
  const documentDiscountType = useAppSelector((state) => state.eDocument.documentDiscountType);
  const documentEInvoiceType = useAppSelector((state) => state.eDocument.documentEInvoiceType);
  const [discountCodeOptions, setDiscountCodeOptions] = useState<SelectOptionType[]>([]);
  const currentSelectOption = discountCodeOptions.find(o => o.value === documentDiscountCode)
  const [localDiscount, setLocalDiscount] = useState<number>(0);
  const [areAllProductSelected, setAllProductSelected] = useState<boolean>(true);
  const [localInputValues, setLocalInputValues] = useState<Record<string, string>>({});
  const [isOtherChargesExpanded, setOtherChargesExpanded] = useState<boolean>(false);
  const localInputValuesRef = useRef(localInputValues);
  const localDiscountRef = useRef(localDiscount);
  const isDiscountCodeSelected = summaryTotals.descuento > 0 && !currentSelectOption
  const discountCodeRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    localInputValuesRef.current = localInputValues;
  }, [localInputValues]);

  useEffect(() => {
    localDiscountRef.current = localDiscount;
  }, [localDiscount]);

  const ids = documentProducts.map((p) => `${p.id}`);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { delay: 150, tolerance: 5 } })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = ids.indexOf(`${active.id}`);
    const newIndex = ids.indexOf(`${over.id}`);
    const newIds = arrayMove(ids, oldIndex, newIndex).map(v => `${v}`);

    dispatch(reorderProductsByIds(newIds));
  };

  useEffect(() => {
    const newValues: Record<string, string> = {};
    documentProducts.forEach(p => {
      newValues[`selected-${p.id}`] = p.selected?.toString() ?? "";
      newValues[`cant-${p.id}`] = p.cant != null ? formatCant(p.cant) : "";
      newValues[`cantFact-${p.id}`] = p.cantFact != null ? formatCant(p.cantFact) : "";
      newValues[`price-${p.id}`] = p.price?.toString() ?? "";
      newValues[`priceFact-${p.id}`] = p.priceFact?.toString() ?? "";
      newValues[`descriptionFact-${p.id}`] = p.descriptionFact?.toString() ?? "";
      newValues[`subtotalFact-${p.id}`] = p.subtotalFact?.toString() ?? "";
      newValues[`description-${p.id}`] = p.description ?? "";
      newValues[`porcentaje_descuento-${p.id}`] = p.porcentaje_descuento?.toString() ?? "0";
    });
    setLocalInputValues(newValues);
  }, [documentProducts]);

  useEffect(() => {
    setLocalDiscount(documentDiscount)
  }, [documentDiscount]);

  useEffect(() => {
    const options = generateSelectOption(DISCOUNT_CODES)
    setDiscountCodeOptions(options)
    const unsubscribe = ScrollToDiscountCodeSelectPubSub.subscribe(scrolToDiscountCode);
    return () => {
      unsubscribe();
    };
  }, []);

  const scrolToDiscountCode = () => {
    if (discountCodeRef.current) {
      discountCodeRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }


  const openOtherCharges = () => {
    OpenDocumentOtherChargesPubSub.publish({ openOffCanvas: true });
  }

  const applyProductUpdate = (
    id: string,
    propName: keyof EDocumentProductDataType,
    value: string | number,
  ) => {
    dispatch(setProductPropertyData({ id, property: propName, value }));

    if (
      propName === "cant" ||
      propName === "price" ||
      propName === "descuento" ||
      propName === "porcentaje_descuento"
    ) {
      const product = (store.getState().eDocument.documentProducts ?? []).find(
        (p) => p.id === id,
      );
      if (product?.exemption) {
        dispatch(clearProductExemptionData(id));
        showAlert(
          "warning",
          "Por el cambio de precio es necesario agregar la exoneracion nuevamente",
        );
      }
    }
  };

  const commitProductFieldFromLocal = (
    productId: string,
    propName: EditableProductField,
    rawValue: string,
    maxValue?: number | false,
    showValidationAlert = true,
  ): boolean => {
    const parsed = parseProductFieldValue(propName, rawValue, maxValue);
    if (!parsed.ok) {
      if (showValidationAlert && parsed.error) {
        showAlert("danger", parsed.error);
      }
      return false;
    }
    applyProductUpdate(productId, propName, parsed.update.value);

    // La descripcion se normaliza al commitear, asi que el input tiene que mostrar lo que
    // de verdad quedo guardado. No alcanza con el efecto que sincroniza desde Redux: si el
    // valor normalizado ya era el que estaba en Redux, el estado no cambia, el efecto no
    // corre y el input se quedaria mostrando el texto sucio.
    const committed = `${parsed.update.value}`;
    if (propName === "description" && committed !== rawValue) {
      setLocalInputValues((prev) => ({
        ...prev,
        [`${propName}-${productId}`]: committed,
      }));
    }
    return true;
  };

  const flushAllPendingEdits = useCallback(() => {
    const eDocumentState = store.getState().eDocument;
    const products = eDocumentState.documentProducts ?? [];

    const pendingDiscount = getPendingDocumentDiscount(
      localDiscountRef.current,
      eDocumentState.documentDiscount || 0,
    );
    if (pendingDiscount !== null) {
      if (pendingDiscount > 0) {
        OpenSetInvoiceDiscountTypePubSub.publish({
          openOffCanvas: true,
          discountType:
            eDocumentState.documentDiscountType || ON_INVOICE_DISCOUNT_TYPE_CODE,
        });
      } else {
        dispatch(setDocumentDiscountType(ON_INVOICE_DISCOUNT_TYPE_CODE));
      }
      dispatch(setDocumentDiscount(pendingDiscount));
    }

    const maxCantByProductId: Record<string, number | false> = {};
    if (documentNoteTable && !priceNote) {
      products.forEach((p) => {
        if (p.id) {
          maxCantByProductId[`${p.id}`] = p.cantFact || false;
        }
      });
    }

    const updates = getPendingProductUpdates(
      localInputValuesRef.current,
      products,
      { documentNoteTable, maxCantByProductId },
    );
    updates.forEach((update) => {
      applyProductUpdate(update.id, update.property, update.value);
    });
  }, [dispatch, documentNoteTable, priceNote, documentProducts]);

  useEffect(() => {
    const unsubscribeFlush = FlushDocumentProductEditsPubSub.subscribe(
      () => flushAllPendingEdits(),
    );
    return () => {
      unsubscribeFlush();
    };
  }, [flushAllPendingEdits]);

  const onProductFieldBlur = (
    productId: string,
    propName: EditableProductField,
    reduxValue: string | number | undefined,
    maxValue?: number | false,
  ) => {
    const key = `${propName}-${productId}`;
    const localValue = localInputValuesRef.current[key];
    if (localValue === undefined) {
      return;
    }

    const reduxStr =
      propName === "description"
        ? `${reduxValue ?? ""}`
        : `${reduxValue ?? (propName === "porcentaje_descuento" ? "0" : "")}`;

    if (localValue === reduxStr) {
      return;
    }

    if (
      propName !== "description" &&
      !isNaN(Number(localValue)) &&
      !isNaN(Number(reduxStr)) &&
      Number(localValue) === Number(reduxStr)
    ) {
      return;
    }

    if (!commitProductFieldFromLocal(productId, propName, localValue, maxValue, false)) {
      setLocalInputValues((prev) => ({
        ...prev,
        [key]: reduxStr,
      }));
    }
  };

  const commitDocumentDiscountFromLocal = (showValidationAlert = true) => {
    const rawValue = `${localDiscountRef.current}`;
    if (!rawValue) {
      if (showValidationAlert) {
        showAlert("danger", "El valor de descuento es obligatoria");
      }
      return false;
    }

    const numericValue = Number(rawValue);
    if (isNaN(numericValue) || numericValue < 0 || numericValue > 100) {
      if (showValidationAlert) {
        showAlert("danger", "El valor de descuento no es valido");
      }
      return false;
    }

    if (numericValue > 0) {
      OpenSetInvoiceDiscountTypePubSub.publish({
        openOffCanvas: true,
        discountType: documentDiscountType || ON_INVOICE_DISCOUNT_TYPE_CODE,
      });
    } else {
      dispatch(setDocumentDiscountType(ON_INVOICE_DISCOUNT_TYPE_CODE));
    }

    dispatch(setDocumentDiscount(numericValue));
    return true;
  };

  const toggleAllProductLine = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = `${event.target.checked}`;
    setAllProductSelected(event.target.checked);
    documentProducts.forEach(dp => {
      dispatch(setProductPropertyData({
        id: `${dp.id}`,
        property: "selected",
        value: checked
      }))
    });
    dispatch(updateCreditNoteData())
  }

  const selectedProductLine = (id: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = `${event.target.checked}`;
    dispatch(setProductPropertyData({
      id,
      property: "selected",
      value: checked
    }))
    dispatch(updateCreditNoteData())
  }
  const updateProductData = (
    id: string | undefined | null,
    propName: EditableProductField,
    event: React.KeyboardEvent<HTMLInputElement>,
    maxValue?: number | false,
  ) => {
    if (event.key === "Enter" && id) {
      event.preventDefault();
      event.stopPropagation();

      const input = event.target as HTMLInputElement;
      if (commitProductFieldFromLocal(id, propName, input.value, maxValue)) {
        setTimeout(() => {
          input.blur();
        });
      }
    }
  };

  const updateDocumentDiscount = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();

      const input = event.target as HTMLInputElement;
      localDiscountRef.current = Number(input.value || 0);
      if (commitDocumentDiscountFromLocal()) {
        setTimeout(() => {
          input.blur();
        });
      }
    }
  };

  const productHasNotEnoughStock = (product: EDocumentProductDataType): boolean => {
    return lowStockProducts.some(lp => lp.productId === product.productId)
  }

  const productHasZeroPrice = (product: EDocumentProductDataType): boolean => {
    return priceZeroProducts.some(lp => lp.productId === product.productId)
  }

  /**
   * Lineas que una validacion previa a emitir marco como no emitibles: sin partida
   * arancelaria en una factura de exportacion, o con el CAByS incoherente con su tipo o
   * su tarifa de IVA. El menu de la fila se pinta en rojo porque desde ahi se corrigen.
   */
  const isLineInvalid = (lineId: string): boolean =>
    noPartidaArancelariaProducts.some(lp => `${lp.id}` === lineId)
    || invalidCabysProducts.some(lp => `${lp.id}` === lineId)

  const onDiscountBlur = () => {
    const pending = getPendingDocumentDiscount(
      localDiscountRef.current,
      documentDiscount,
    );
    if (pending === null) {
      return;
    }
    if (!commitDocumentDiscountFromLocal(false)) {
      setLocalDiscount(documentDiscount);
    }
  };

  const onProductSelected = async (product: EDocumentProductDataType, previousProduct: EDocumentProductDataType) => {
    const currentProduct = await ProductApi.get(`${product.productId}`);
    if (currentProduct && currentProduct.costo) {
      if (Number(previousProduct?.price || 0) < currentProduct.costo) {
        OpenAddProductPubSub.publish({ openOffCanvas: false })
        ConfirmActionPubSub.publish({
          openOffCanvas: true,
          confirmText: (
            <>
              <p>El costo del producto que va a ingresar es menor al costo actual presente en la Base de datos.</p>
              <p><b>Desea actualizar el Precio actual del articulo en la base de datos?</b></p>
              {pricemanaged == FORMULA_PRICES_MANAGEMENT_CODE && (
                <Alert color="warning">
                  Debido a que los precios de los productos se estan manejando actualmente basados en el <b>costo de los productos</b>,
                  el cambio en el costo de este producto afectara los <b>precios del mismo. </b>
                </Alert>
              )}
            </>
          ),
          onCancelCalback: () => {
            dispatch(setProductPropertiesData({
              id: `${previousProduct.id}`,
              price: currentProduct.costo,
              productId: product.productId
            }))
          },
          onConfirmCalback: () => {
            updateProductCost({
              id: `${previousProduct.id}`,
              property: "productId",
              value: product.productId
            })
          }
        })
      }
      else {
        updateProductCost({
          id: `${previousProduct.id}`,
          property: "productId",
          value: product.productId
        })
      }
    }
  }

  const updateProductCost = (productData: ProductPropertyDataProp) => {
    dispatch(setProductPropertyData(productData))
    OpenAddProductPubSub.publish({ openOffCanvas: false })
  }

  const renderProductLine = (): ReactNode[] => {
    return documentProducts.filter(p => !p.toDelete).map(p => {
      // Hook para handle (solo en la celda de código)

      return (
        <SortableRow key={p.id} id={`${p.id}`} testId={`line-row-${p.id}`}>
          {({ attributes, listeners }) => (
            <>
              <td>
                {documentNoteTable ? " " : (
                  <Button data-testid={`line-remove-${p.id}`} type="button" size="sm" color="danger" onClick={() => dispatch(removeProduct(`${p.id}`))}>
                    <FontAwesomeIcon icon={faXmark} />
                  </Button>
                )}
                {documentNoteTable && priceNote ? (
                  <Input
                    data-testid={`line-select-${p.id}`}
                    className={styles.selectCheckbox}
                    type="checkbox"
                    checked={localInputValues[`selected-${p.id}`] == "true"}
                    onChange={(e) => selectedProductLine(`${p.id}`, e)}
                  />
                ) : ""}
              </td>
              <td>
                <div
                  {...attributes}
                  {...listeners}
                  style={{ cursor: "grab", userSelect: "none" }}
                  title="Mantén pulsado y arrastra para reordenar"
                >
                  <div className={cx({
                    [styles.exentoBadge]: p.exento,
                    [styles.exoneradoBadge]: p.exemption,
                    [styles.extraCostBadge]: p.extraCost
                  })}></div>
                  {p.codigo}
                </div>
              </td>
              <td>
                {providerDocument ? localInputValues[`description-${p.id}`] : ''}
                {!providerDocument && (documentNoteTable ?
                  (priceNote ? localInputValues[`descriptionFact-${p.id}`] : localInputValues[`description-${p.id}`]) :
                  (
                    <Input
                      data-testid={`line-description-${p.id}`}
                      type="text"
                      value={localInputValues[`description-${p.id}`] ?? ""}
                      onChange={(e) => setLocalInputValues((prev) => ({
                        ...prev,
                        [`description-${p.id}`]: e.target.value,
                      }))}
                      onKeyDown={(e) => updateProductData(p.id, "description", e)}
                      onBlur={() => onProductFieldBlur(`${p.id}`, "description", p.description)}
                    />
                  )
                )}
              </td>
              <td>
                {documentNoteTable ? localInputValues[`cantFact-${p.id}`] :
                  (
                    <Input
                      data-testid={`line-cant-${p.id}`}
                      invalid={productHasNotEnoughStock(p)}
                      type="number"
                      step="0.001"
                      min="0"
                      value={localInputValues[`cant-${p.id}`] ?? ""}
                      onChange={(e) => setLocalInputValues((prev) => ({
                        ...prev,
                        [`cant-${p.id}`]: e.target.value,
                      }))}
                      onKeyDown={(e) => updateProductData(p.id, "cant", e)}
                      onBlur={() => onProductFieldBlur(`${p.id}`, "cant", p.cant)}
                    />
                  )}
              </td>
              {documentNoteTable && !priceNote && (
                <td>
                  <Input
                    data-testid={`line-cant-returned-${p.id}`}
                    type="number"
                    step="0.001"
                    min="0"
                    value={localInputValues[`cant-${p.id}`] ?? ""}
                    onChange={(e) => setLocalInputValues((prev) => ({
                      ...prev,
                      [`cant-${p.id}`]: e.target.value,
                    }))}
                    onKeyDown={(e) =>
                      updateProductData(p.id, "cant", e, p.cantFact || false)
                    }
                    onBlur={() =>
                      onProductFieldBlur(`${p.id}`, "cant", p.cant, p.cantFact || false)
                    }
                  />
                </td>
              )}
              <td>
                {documentNoteTable ?
                  (priceNote ? localInputValues[`priceFact-${p.id}`] : localInputValues[`price-${p.id}`]) : (
                    <Input
                      data-testid={`line-price-${p.id}`}
                      type="number"
                      value={localInputValues[`price-${p.id}`] ?? ""}
                      onChange={(e) => setLocalInputValues((prev) => ({
                        ...prev,
                        [`price-${p.id}`]: e.target.value,
                      }))}
                      onKeyDown={(e) => updateProductData(p.id, "price", e)}
                      onBlur={() => onProductFieldBlur(`${p.id}`, "price", p.price)}
                    />
                  )}
              </td>
              <td>
                <span
                  data-testid={`line-subtotal-${p.id}`}
                  className={cx({
                    [styles.priceError]: productHasZeroPrice(p) && p.subtotal == 0
                  })}>
                  {documentNoteTable && priceNote ? p.subtotalFact : p.subtotal}
                </span>
              </td>
              {!providerDocument && (<td>
                {documentNoteTable ? localInputValues[`porcentaje_descuento-${p.id}`] ?? "" : (
                  <InputGroup>
                    <Input
                      data-testid={`line-discount-${p.id}`}
                      type="text"
                      value={localInputValues[`porcentaje_descuento-${p.id}`] ?? ""}
                      onChange={(e) => setLocalInputValues((prev) => ({
                        ...prev,
                        [`porcentaje_descuento-${p.id}`]: e.target.value,
                      }))}
                      onKeyDown={(e) => updateProductData(p.id, "porcentaje_descuento", e)}
                      onBlur={() =>
                        onProductFieldBlur(
                          `${p.id}`,
                          "porcentaje_descuento",
                          p.porcentaje_descuento,
                        )
                      }
                    />
                    <InputGroupText>
                      %
                    </InputGroupText>
                  </InputGroup>
                )}
              </td>
              )}
              <td>
                {(providerDocument && !hideSelectProduct) && (
                  <AddProductToDocumentOffCanvas
                    hideTemplate={true}
                    useProductCost={true}
                    hideTemporal={true}
                    productSelection={true}
                    defaultSearchType="general"
                    btnColor={p.productId ? "success" : "primary"}
                    onProductSelect={(product) => onProductSelected(product, p)}
                  >
                    Seleccionar
                  </AddProductToDocumentOffCanvas>
                )}
                {documentNoteTable || providerDocument ? "" : (
                  <DropdownActionsMenu
                    isInvalid={isLineInvalid(`${p.id}`)}
                    actions={[
                      {
                        actionTitle: "Ajuste de Porcentaje de impuestos",
                        onClick: () => OpenProductTaxChangePubSub.publish({
                          openOffCanvas: true,
                          defaultData: {
                            productId: p.id,
                            taxPercentage: p.codigo_tarifa,
                            taxType: p.tipo_impuesto
                          }
                        })
                      },
                      {
                        actionTitle: `Linea ${p.exento ? "no" : ""} Exenta`,
                        onClick: () => dispatch(setProductExempted({
                          id: `${p.id}`,
                          exempted: !p.exento
                        }))
                      },
                      {
                        actionTitle: `${p.exemption ? "Editar" : "Agregar"} Exoneracion`,
                        onClick: () => {
                          let defaultData = {
                            monto_impuesto: p.impuestos,
                            porcentaje_compra: p.porcentaje_impuesto,
                            max_porcentaje_compra: p.porcentaje_impuesto
                          }
                          if (p.exemption) {
                            let exemptionSt = `${p.exemption}`;
                            if (p.exemption instanceof Object) {
                              exemptionSt = JSON.stringify(p.exemption);
                            }
                            const exemptionData = JSON.parse(exemptionSt)
                            defaultData = {
                              ...exemptionData
                            }
                          }
                          OpenDocumentExemptionPubSub.publish({
                            openOffCanvas: true,
                            productId: `${p.id}`,
                            defaultData,
                            lineSubtotal: p.subtotal || 0
                          })
                        }
                      },
                      {
                        isVisible: EINVOICE_EXPORT_INVOICE_TYPE_CODE == documentEInvoiceType && p.tipo_producto == MERCHANDISE_PRODUCT_TYPE_CODE,
                        actionTitle: `Asignar Partida Arancelaria`,
                        onClick: () => OpenProductPartidaArancelariaPubSub.publish({
                          openOffCanvas: true,
                          productLineId: `${p.id}`,
                          partidaArancelaria: `${p.partidaArancelaria || ""}`
                        })
                      },
                      {
                        isVisible: !!p.exemption,
                        actionTitle: `Eliminar Exoneracion`,
                        onClick: () => {
                          dispatch(clearProductExemptionData(`${p.id}`))
                        }
                      },
                      {
                        actionTitle: "Dividir Linea",
                        onClick: () => dispatch(splitProduct(`${p.id}`))
                      },
                      // {
                      //   actionTitle: "Ver Historial de Precio de Venta",
                      //   onClick: () => alert("test")
                      // }
                    ]}
                  />
                )}
              </td>
            </>
          )
          }
        </SortableRow >
      )
    });

  }

  const toogleExemptDocument = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      dispatch(setDocumentExempted(true))
      dispatch(makeProductsExempted())
    } else {
      dispatch(setDocumentExempted(false))
      dispatch(resetProductsExemption())
    }
  }

  const onProductTaxChanged = (taxChange?: ProductTaxChangeDataType) => {
    const codigoTarifa = taxChange?.taxPercentage || "";
    const tipoImpuesto = taxChange?.taxType || "";

    dispatch(setProductPropertiesData({
      id: `${taxChange?.productId}`,
      codigo_tarifa: codigoTarifa,
      porcentaje_impuesto: TAXES_PERCENTAGES_VALUES[codigoTarifa],
      tipo_impuesto: tipoImpuesto
    }))
  }

  const onDiscountTypeChanged = (discountTypeForm?: InvoiceDiscountTypeDataType) => {
    dispatch(setDocumentDiscountType(`${discountTypeForm?.discountType || ON_INVOICE_DISCOUNT_TYPE_CODE}`))
  }

  return (
    <>
      <Table
        className={styles.productTitle}
        striped
        size="sm">
        <thead className={styles.tableHeader}>
          <tr>
            <td>
              {documentNoteTable && priceNote ? (
                <Input
                  data-testid="line-select-all"
                  className={styles.selectCheckbox}
                  type="checkbox"
                  checked={areAllProductSelected}
                  onChange={(e) => toggleAllProductLine(e)}
                />
              ) : ""}
            </td>
            <td className={styles.codigo}>Codigo</td>
            <td className="col-md-4">Description</td>
            <td>Cantidad</td>
            {documentNoteTable && !priceNote && (
              <td className="col-md-1 text-center">Cantidad Devuelta</td>
            )}
            <td className={styles.price}>{providerDocument ? 'Costo' : 'Precio'}</td>
            <td className={styles.subtotal}>Subtotal</td>
            {!providerDocument && (<td className={styles.discount}>% Desc</td>)}
            <td></td>
          </tr>
        </thead>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <tbody>
              {renderProductLine()}
            </tbody>
          </SortableContext>
        </DndContext>
      </Table >

      {!providerDocument && (
        <>
          <Row>
            <Col sm="5" lg="7">
              <div><div className={styles.exentoBadge}></div> Productos exentos de impuestos</div>
              <div><div className={styles.exoneradoBadge}></div> Productos exonerados de impuestos</div>
              <div><div className={styles.extraCostBadge}></div> Productos con Costo de envio agregado</div>
              {!documentNoteTable && (
                <>
                  <FormGroup className="mt-3" check>
                    <Input data-testid="document-exempt" id="exempt-Invoices-check" onChange={toogleExemptDocument} type="checkbox" />
                    {' '}
                    <Label for="exempt-Invoices-check" check>
                      Factura Exenta
                    </Label>
                  </FormGroup>
                  <div className="mt-2">
                    <Button type="button" color="primary" onClick={() => dispatch(removeZeroProductLines())}>
                      Eliminar lineas en cero
                    </Button>
                  </div>
                </>
              )}
            </Col>
            <Col sm="7" lg="5">
              <div className="totales">
                <Table className={styles.summaryTable}>
                  <thead>
                    <tr>
                      <th className="col-8"></th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td >
                        <div className="offset-md-2">
                          Subtotal
                        </div>
                      </td>
                      <td>
                        <span>{currencySymbol} </span>
                        <span data-testid="summary-subtotal" data-amount={summaryTotals.subtotal}>
                          {formatMoneyAmount(summaryTotals.subtotal)}
                        </span>
                      </td>
                    </tr>
                    {!documentNoteTable && (<tr>
                      <td >
                        <Col
                          className={styles.totalTitle}
                          md={{
                            offset: 2
                          }} >
                          Descuento
                        </Col>
                      </td>
                      <td>
                        <InputGroup>
                          <Input
                            data-testid="document-discount"
                            type="number"
                            value={localDiscount}
                            onChange={(e) => setLocalDiscount(Number(e.target.value || 0))}
                            onKeyDown={updateDocumentDiscount}
                            onBlur={onDiscountBlur}
                          />
                          <InputGroupText>
                            %
                          </InputGroupText>
                        </InputGroup>
                      </td>
                    </tr>
                    )}
                    {!documentNoteTable && summaryTotals.descuento > 0 && documentDiscountType == ON_INVOICE_DISCOUNT_TYPE_CODE && (<tr>
                      <td colSpan={2} >
                        <Row>
                          <Col
                            className={styles.totalTitle}
                            sm={{
                              offset: 1,
                              size: 4
                            }} >
                            Codigo Descuento
                          </Col>
                          <Col sm={{
                            size: 7
                          }} >
                            <div data-testid="document-discount-code" ref={discountCodeRef}>
                              <InputGroup
                                className={cx({ "is-invalid": isDiscountCodeSelected })} >
                                <Select
                                  className={cx({
                                    [styles.selectDiscount]: true,
                                    [styles.selectDiscountError]: isDiscountCodeSelected
                                  })}
                                  name="discountCode"
                                  placeholder="Seleccion uno"
                                  value={currentSelectOption || null}
                                  options={discountCodeOptions}
                                  onChange={(newValue) => { dispatch(setDocumentDiscountCode(`${newValue?.value}` as keyof typeof DISCOUNT_CODES)) }}
                                />
                              </InputGroup>
                              {isDiscountCodeSelected && (<div className="invalid-feedback">
                                Este campo es requerido
                              </div>)}
                            </div>
                          </Col>
                        </Row>
                      </td>
                    </tr>
                    )}
                    <tr>
                      <td >
                        <div className="offset-md-2">
                          {documentNoteTable ? "Descuento" : "Valor Descuento"}
                        </div>
                      </td>
                      <td>
                        <span>{currencySymbol} </span>
                        <span data-testid="summary-descuento" data-amount={summaryTotals.descuento}>
                          <span>{formatMoneyAmount(summaryTotals.descuento)}</span>
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td >
                        <div className="offset-md-2">
                          I.V.A.
                        </div>
                      </td>
                      <td>
                        <span>{currencySymbol} </span>
                        <span data-testid="summary-impuestos" data-amount={summaryTotals.impuestos}>
                          <span>{formatMoneyAmount(summaryTotals.impuestos)}</span>
                        </span>
                      </td>
                    </tr>
                    {showOtherCharges && (
                      <tr>
                        <td>
                          <div className="offset-md-2">
                            {documentOtherCharges.length === 0 ? (
                              <button
                                type="button"
                                className={styles.otherChargesLink}
                                data-testid="other-charges-add-link"
                                onClick={openOtherCharges}
                              >
                                + Agregar otros cargos
                              </button>
                            ) : (
                              <button
                                type="button"
                                className={styles.otherChargesLink}
                                data-testid="other-charges-toggle"
                                onClick={() => setOtherChargesExpanded((prev) => !prev)}
                              >
                                Otros cargos ({documentOtherCharges.length})
                              </button>
                            )}
                          </div>
                        </td>
                        <td>
                          <span data-testid="other-charges-total" data-amount={summaryTotals.otrosCargos}>
                            <span>{currencySymbol} </span>
                            <span>{formatMoneyAmount(summaryTotals.otrosCargos)}</span>
                          </span>
                        </td>
                      </tr>
                    )}
                    {showOtherCharges && isOtherChargesExpanded && documentOtherCharges.map((charge) => (
                      <tr key={charge.id} data-testid={`other-charges-detail-${charge.id}`}>
                        <td>
                          <div className={cx([styles.otherChargeDetail, "offset-md-2"])}>
                            {getOtherChargeTypeLabel(charge.tipoDocumentoOC)}
                            {charge.mode === "percent" ? ` (${charge.porcentajeOC ?? 0}%)` : ""}
                          </div>
                        </td>
                        <td>
                          <div className={styles.otherChargeDetail}>
                            <span>{currencySymbol} </span>
                            <span>{formatMoneyAmount(resolveChargeAmount(charge, otherChargesBase))}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {showOtherCharges && isOtherChargesExpanded && documentOtherCharges.length > 0 && (
                      <tr>
                        <td colSpan={2}>
                          <div className="offset-md-2">
                            <button
                              type="button"
                              className={styles.otherChargesLink}
                              data-testid="other-charges-edit-link"
                              onClick={openOtherCharges}
                            >
                              Editar otros cargos
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td>
                        <div className={cx([styles.summaryAmountHighlight, "offset-md-2"])}>
                          Total
                        </div>
                      </td>
                      <td>
                        <div
                          className={styles.summaryAmountHighlight}
                          data-testid="summary-total"
                          data-total={summaryTotals.total}
                        >
                          <span>{currencySymbol} </span>
                          <span ><span>{formatMoneyAmount(summaryTotals.total)}</span></span>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </Col>
          </Row>
          <SetProductPartidaArancelariaOffCanvas />
          <ChangeProductTaxOffCanvas onTaxChanged={onProductTaxChanged} />
          <SetInvoiceDiscountTypeOffCanvas onDiscountTypeChanged={onDiscountTypeChanged} />
        </>
      )}
      <ActionConfirmation
        title="Confirmar"
      />
    </>

  )
}

export default EdocumentProductList;